import { useEffect, useState } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { SliderWithTitle } from '@hivebrite/design-system/Slider';
import type { Breakpoints } from '@hivebrite/design-system/Theme';
import { useBreakpoint } from '@hivebrite/design-system/Theme';

import type { SliderLayoutsProps } from 'hb-builder/components/BuilderComponents/dynamic/Slider/SliderLayouts';
import { SLIDER_TYPES } from 'hb-builder/components/BuilderComponents/dynamic/Slider/SliderLayouts/types';
import { perPageBreakpoints } from 'hb-builder/components/BuilderComponents/dynamic/Slider/utils/breakpoints';
import { getHeadline } from 'hb-builder/components/BuilderComponents/dynamic/Slider/utils/getHeadline';
import BuilderThemeProvider, {
  CONTAINER_TYPE,
  VIEW_AS,
} from 'hb-builder/components/common/BuilderThemeProvider';
import { getArrowTopPositionInSlider } from 'hb-builder/utils/getArrowTopPositionInSlider';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { TopicLoadingCard } from 'hb-react/shared/apps/TopicsIndexV2/components/TopicLoadingCard';
import {
  PARAM_FOLLOWED_BY_USER,
  PARAM_SUGGESTED_FOR_USER,
} from 'hb-react/shared/apps/TopicsIndexV2/constants/queryParams';
import type { ITopic } from 'hb-react/shared/apps/TopicsIndexV2/types';
import { APP_TYPE } from 'hb-react/types/appType';

import BuilderTopicCard from './BuilderTopicCard';

export type SliderTopicsWrapperProps = {
  data: ITopic[];
  isLoading: boolean;
  params: Record<
    'query',
    {
      type: 'followed_by_user' | 'last_created' | 'suggested_for_user';
      categories: number[];
    }
  >;
} & Omit<SliderLayoutsProps, 'dynamicAttributes' | 'isPublic' | 'type'>;

const SLIDER_CARD_HEIGHT = {
  _: 243,
  m: 192,
  l: 167,
  xl: 180,
};

const TopicsWrapper = ({
  app,
  data: fetchedTopics,
  isLoading,
  item,
  params,
  networkRootUrl,
}: SliderTopicsWrapperProps) => {
  const numberOfItems =
    item?.attributes?.dynamicAttributes.query?.numberOfItems;
  const [topics, setTopics] = useState(fetchedTopics);
  const breakpoint = useBreakpoint();
  const { currentUser } = useDefaultAppContexts();
  const isBackOffice = app === APP_TYPE.BACKOFFICE;
  const isConnected = !!currentUser || isBackOffice;
  const navTopPosition = getArrowTopPositionInSlider(
    SLIDER_CARD_HEIGHT,
  ) as Breakpoints;

  useEffect(() => {
    setTopics(fetchedTopics);
  }, [fetchedTopics]);

  const title = getHeadline(item, SLIDER_TYPES.TOPICS);

  const renderTopicsItem = (topic: ITopic) => (
    <BuilderThemeProvider
      containerType={CONTAINER_TYPE.CARD}
      key={item.id}
      viewAs={VIEW_AS.USER}
    >
      <BuilderTopicCard
        isBackOffice={isBackOffice}
        isConnected={isConnected}
        networkRootUrl={networkRootUrl}
        setTopics={setTopics}
        topic={topic}
        topics={topics}
      />
    </BuilderThemeProvider>
  );

  const linkText =
    breakpoint !== 's' ? s__('Builder|See all groups') : s__('Builder|See all');
  const seeAllLinkHref = [
    PARAM_FOLLOWED_BY_USER,
    PARAM_SUGGESTED_FOR_USER,
  ].includes(params?.query?.type)
    ? `${networkRootUrl}/topics?${params.query.type}=true`
    : `${networkRootUrl}/topics`;

  return (
    <Box flexDirection="column" py={4}>
      <SliderWithTitle
        key={`groups-slider-with-${numberOfItems}-items`}
        title={title}
        perPageBreakpoints={perPageBreakpoints[SLIDER_TYPES.TOPICS]}
        items={topics}
        ItemComponent={renderTopicsItem}
        link={{
          target: '_self',
          text: linkText,
          url: isBackOffice ? '#' : seeAllLinkHref,
        }}
        navTopPosition={navTopPosition}
        total={topics.length}
        loading={isLoading}
        LoadingItemComponent={BuilderTopicLoadingCard}
      />
    </Box>
  );
};
export default TopicsWrapper;

const BuilderTopicLoadingCard = ({ isConnected }) => (
  <TopicLoadingCard
    isConnectedUser={isConnected}
    noDescriptionPlaceholder={false}
  />
);
